// ============================================================================
// sportFixtures.ts — per-match page model for every non-football sport.
//
// Football fixtures live at /fixtures/:slug (driven by `predictions`). Every
// other sport in the odds catalog (NFL, NBA, MLB, NHL, cricket, tennis, MMA,
// rugby, handball, golf…) is stored in `us_sports_predictions`, one row per
// market. This module turns a group of those rows into a single, citable match
// page: slug, metadata, structured editorial copy and FAQs.
//
// Deliberately React-free and dependency-light so the sitemap generator and the
// build-time prerenderer can import it directly.
// ============================================================================

import { findUsSport, type UsSport, type MarketType } from "./oddsSports";

export interface SportFixtureRow {
  id: string;
  sport: string;
  home_team: string;
  away_team: string;
  commence_time: string;
  market_type: MarketType | string;
  predicted_outcome: string;
  model_probability?: number | null;
  implied_probability?: number | null;
  edge?: number | null;
  confidence_score?: number | null;
  odds?: number | null;
  point?: number | null;
  risk_level?: string | null;
  explanation?: string | null;
  actual_outcome?: string | null;
  was_correct?: boolean | null;
  updated_at?: string | null;
  created_at?: string | null;
}

// ---------------------------------------------------------------------------
// Slugs
// ---------------------------------------------------------------------------

export function slugifyTeam(value: string): string {
  return (value || "")
    .toLowerCase()
    .trim()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

/** `home-vs-away-YYYY-MM-DD` — stable, readable, permanent. */
export function buildSportFixtureSlug(
  home: string,
  away: string,
  commence?: string | null,
): string {
  const date = (commence || "").slice(0, 10);
  const valid = /^\d{4}-\d{2}-\d{2}$/.test(date) ? date : "";
  return `${slugifyTeam(home)}-vs-${slugifyTeam(away)}${valid ? `-${valid}` : ""}`;
}

export interface ParsedSportFixtureSlug {
  homeSlug: string;
  awaySlug: string;
  date: string;
}

export function parseSportFixtureSlug(slug: string): ParsedSportFixtureSlug | null {
  const m = /^(.+?)-vs-(.+?)-(\d{4}-\d{2}-\d{2})$/.exec(slug || "");
  if (!m) return null;
  return { homeSlug: m![1]!, awaySlug: m![2]!, date: m![3]! };
}

/** Canonical path for a sport match page. */
export function sportFixturePath(sportSlug: string, slug: string): string {
  return `/${sportSlug}-predictions/match/${slug}`;
}

export function sportFixturePathFor(row: SportFixtureRow): string {
  return sportFixturePath(
    row.sport,
    buildSportFixtureSlug(row.home_team, row.away_team, row.commence_time),
  );
}

// ---------------------------------------------------------------------------
// Grouping
// ---------------------------------------------------------------------------

/** Group rows into one bucket per match (same teams, same UTC day). */
export function groupByMatch(rows: SportFixtureRow[]): Map<string, SportFixtureRow[]> {
  const out = new Map<string, SportFixtureRow[]>();
  for (const r of rows) {
    if (!r.home_team || !r.away_team || !r.commence_time) continue;
    const key = `${r.sport}::${buildSportFixtureSlug(r.home_team, r.away_team, r.commence_time)}`;
    const bucket = out.get(key);
    if (bucket) bucket.push(r);
    else out.set(key, [r]);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Display helpers
// ---------------------------------------------------------------------------

/** Is this a "neutral pairing" sport (tennis/MMA/golf) rather than home/away? */
export function isNeutralPairing(sport: UsSport | undefined): boolean {
  if (!sport) return false;
  return (
    sport.category === "tennis" ||
    sport.category === "combat" ||
    sport.category === "golf" ||
    sport.marketShape === "outrights"
  );
}

/** Match title: "Away @ Home" for US leagues, "A vs B" for neutral pairings. */
export function matchTitle(row: SportFixtureRow, sport?: UsSport): string {
  if (isNeutralPairing(sport)) return `${row.home_team} vs ${row.away_team}`;
  return `${row.away_team} @ ${row.home_team}`;
}

/** Human market label using the sport's own vocabulary. */
export function marketLabel(sport: UsSport | undefined, marketType: string): string {
  const found = sport?.markets.find((m) => m.marketType === marketType);
  if (found) return found.label;
  switch (marketType) {
    case "moneyline":
      return "Moneyline";
    case "spread":
      return "Spread / Handicap";
    case "total":
      return "Over/Under Total";
    case "outright":
      return "Outright Winner";
    default:
      return marketType;
  }
}

/** Outcome including its line, e.g. "Lakers -3.5", "Over 45.5". */
export function outcomeWithLine(row: SportFixtureRow): string {
  if (row.point == null || row.market_type === "moneyline" || row.market_type === "outright") {
    return row.predicted_outcome;
  }
  if (row.market_type === "total") {
    return `${row.predicted_outcome} ${Math.abs(row.point)}`;
  }
  const sign = row.point > 0 ? `+${row.point}` : `${row.point}`;
  return `${row.predicted_outcome} ${sign}`;
}

function pct(v: number | null | undefined): string | null {
  if (v == null || !Number.isFinite(v)) return null;
  return `${Math.round(v * 100)}%`;
}

function fmtOdds(v: number | null | undefined): string | null {
  if (v == null || !Number.isFinite(v) || v <= 0) return null;
  return v.toFixed(2);
}

function utcDate(iso: string | null | undefined): string {
  if (!iso) return "TBC";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "TBC";
  return d.toLocaleDateString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}

function utcTime(iso: string | null | undefined): string {
  if (!iso) return "kick-off time to be confirmed";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "kick-off time to be confirmed";
  return `${d.toISOString().slice(11, 16)} UTC`;
}

/** Best (highest-confidence) row in the group — drives the headline pick. */
export function primaryRow(rows: SportFixtureRow[]): SportFixtureRow {
  return [...rows].sort(
    (a, b) => (b.confidence_score ?? 0) - (a.confidence_score ?? 0),
  )[0]!;
}

// ---------------------------------------------------------------------------
// Metadata
// ---------------------------------------------------------------------------

export interface SportFixtureMeta {
  h1: string;
  title: string;
  description: string;
  keywords: string;
  canonicalPath: string;
  ogTitle: string;
  ogDescription: string;
}

/**
 * Metadata for a match page. Titles stay under 60 chars and descriptions under
 * 160 so Google renders them whole; the primary keyword ("<match> prediction")
 * always leads.
 */
export function sportFixtureMeta(
  rows: SportFixtureRow[],
  sport?: UsSport,
): SportFixtureMeta {
  const anchor = primaryRow(rows);
  const resolved = sport ?? findUsSport(anchor.sport);
  const label = resolved?.label ?? anchor.sport.toUpperCase();
  const pair = matchTitle(anchor, resolved);
  const day = (anchor.commence_time || "").slice(0, 10);
  const slug = buildSportFixtureSlug(anchor.home_team, anchor.away_team, anchor.commence_time);
  const canonicalPath = sportFixturePath(anchor.sport, slug);

  const pick = outcomeWithLine(anchor);
  const odds = fmtOdds(anchor.odds);
  const conf = anchor.confidence_score != null ? Math.round(anchor.confidence_score) : null;

  const h1 = `${pair} Prediction, Odds & Analysis — ${label}${day ? `, ${day}` : ""}`;

  // 60-char budget: keep the pairing + the primary keyword, drop the brand
  // suffix when the pairing is long rather than letting Google truncate it.
  const shortTitle = `${pair} Prediction & Odds`;
  const title =
    shortTitle.length <= 45 ? `${shortTitle} | Statlign` : shortTitle.slice(0, 57);

  const bits: string[] = [`${pair} ${label} prediction`];
  if (pick) bits.push(`our model backs ${pick}`);
  if (odds) bits.push(`at ${odds}`);
  if (conf != null) bits.push(`${conf}% confidence`);
  const description = `${bits.join(" — ")}. Full odds, model probability, value edge and reasoning.`
    .replace(/\s+/g, " ")
    .slice(0, 158);

  const l = label.toLowerCase();
  const p = pair.toLowerCase();
  const keywords = [
    `${p} prediction`,
    `${p} odds`,
    `${p} betting tips`,
    `${p} pick`,
    `${l} predictions`,
    `${l} betting tips`,
    `${l} odds today`,
  ].join(", ");

  return {
    h1,
    title,
    description,
    keywords,
    canonicalPath,
    ogTitle: `${pair} — ${label} Prediction`.slice(0, 60),
    ogDescription: description.slice(0, 110),
  };
}

// ---------------------------------------------------------------------------
// Editorial copy
// ---------------------------------------------------------------------------

export interface EditorialSection {
  heading: string;
  paragraphs: string[];
}

const CATEGORY_CONTEXT: Record<string, string> = {
  "american-football": "possession efficiency, red-zone conversion and turnover margin",
  basketball: "pace, offensive and defensive efficiency and rest days",
  baseball: "starting pitching, bullpen usage and park factors",
  hockey: "goaltending form, special-teams efficiency and schedule fatigue",
  combat: "stylistic matchup, reach, takedown defence and recent activity",
  cricket: "conditions, toss impact, batting depth and bowling economy",
  rugby: "set-piece dominance, discipline and territory",
  "aussie-rules": "clearance work, inside-50 entries and scoring accuracy",
  handball: "shooting efficiency, goalkeeper save rate and tempo",
  lacrosse: "faceoff win rate, shot volume and goalie form",
  tennis: "surface suitability, serve hold rate and recent match load",
  golf: "course fit, recent scoring average and strokes-gained profile",
};

/**
 * Long-form, data-grounded analysis for one match. Every sentence is derived
 * from the stored row values — no invented statistics, no filler. Reliably
 * lands in the 400–600 word band that keeps the page clear of thin-content
 * and AdSense low-value thresholds.
 */
export function sportFixtureEditorial(
  rows: SportFixtureRow[],
  sport?: UsSport,
): EditorialSection[] {
  const anchor = primaryRow(rows);
  const resolved = sport ?? findUsSport(anchor.sport);
  const label = resolved?.label ?? anchor.sport.toUpperCase();
  const pair = matchTitle(anchor, resolved);
  const neutral = isNeutralPairing(resolved);
  const dateText = utcDate(anchor.commence_time);
  const timeText = utcTime(anchor.commence_time);
  const factors =
    CATEGORY_CONTEXT[resolved?.category ?? ""] ??
    "recent results, scoring trends and market movement";

  const sections: EditorialSection[] = [];

  // --- 1. Match overview -------------------------------------------------
  const overview: string[] = [];
  overview.push(
    neutral
      ? `${pair} is scheduled in ${label} on ${dateText}, starting at ${timeText}. Statlign's model has priced this matchup from the live market and ranked every available selection by value.`
      : `${anchor.away_team} travel to face ${anchor.home_team} in ${label} on ${dateText}, with the first whistle at ${timeText}. Statlign has modelled the fixture across every market the book is offering.`,
  );
  overview.push(
    `We build each ${label} price by stripping the bookmaker's margin out of the posted odds to recover a true implied probability, then adjusting that baseline for ${factors}. The gap between our adjusted probability and the market's implied probability is the value edge, and it is the only reason a selection appears on this page.`,
  );
  overview.push(
    `This match currently carries ${rows.length} modelled ${rows.length === 1 ? "selection" : "selections"} across ${new Set(rows.map((r) => r.market_type)).size} ${new Set(rows.map((r) => r.market_type)).size === 1 ? "market" : "markets"}. Every number below is the value recorded at generation time; live prices move, so always confirm the current odds with your bookmaker before staking.`,
  );
  sections.push({ heading: `${pair}: match overview`, paragraphs: overview });

  // --- 2. Market-by-market breakdown -------------------------------------
  const marketParas: string[] = [];
  const byMarket = new Map<string, SportFixtureRow[]>();
  for (const r of rows) {
    const b = byMarket.get(String(r.market_type));
    if (b) b.push(r);
    else byMarket.set(String(r.market_type), [r]);
  }
  for (const [mt, group] of byMarket) {
    const top = primaryRow(group);
    const parts: string[] = [];
    parts.push(`${marketLabel(resolved, mt)}: the model's selection is ${outcomeWithLine(top)}.`);
    const odds = fmtOdds(top.odds);
    if (odds) parts.push(`The recorded price is ${odds}.`);
    const model = pct(top.model_probability);
    const implied = pct(top.implied_probability);
    if (model && implied) {
      parts.push(
        `Our model gives this outcome a ${model} chance against a market-implied ${implied}, which is where the value comes from.`,
      );
    } else if (model) {
      parts.push(`Our model gives this outcome a ${model} chance of landing.`);
    }
    if (top.edge != null && Number.isFinite(top.edge)) {
      parts.push(`That works out to a ${(top.edge * 100).toFixed(1)}% value edge.`);
    }
    if (top.confidence_score != null) {
      parts.push(
        `Confidence is scored at ${Math.round(top.confidence_score)} out of 100, reflecting how much supporting data the engine had for this call.`,
      );
    }
    if (top.risk_level) {
      parts.push(`Risk profile: ${String(top.risk_level).toLowerCase()}.`);
    }
    if (top.explanation) parts.push(top.explanation);
    marketParas.push(parts.join(" "));
  }
  sections.push({
    heading: `Market-by-market ${label} analysis`,
    paragraphs: marketParas,
  });

  // --- 3. How to read the numbers ---------------------------------------
  sections.push({
    heading: "How to read these numbers",
    paragraphs: [
      `Model probability is our estimate of how often this outcome lands if the match were replayed many times. Implied probability is what the bookmaker's price says — the reciprocal of decimal odds, before we remove the margin. When our probability sits meaningfully above the market's, the selection is priced too generously and carries positive expected value.`,
      `The value edge quantifies that gap in percentage-point terms. A small edge on a heavy favourite and a large edge on a longshot are very different propositions, which is why we publish the confidence score alongside it: confidence reflects data depth and model agreement, not certainty of a win. A high edge with low confidence is a thin signal, not a strong one.`,
      `Stake sizing should always follow the edge rather than the excitement. Flat staking or a fractional Kelly approach protects a bankroll far better than chasing the biggest price on the page. No selection here is guaranteed; the model's job is to find prices that are wrong, not results that are certain.`,
    ],
  });

  // --- 4. Results / verification ----------------------------------------
  const settled = rows.filter((r) => r.actual_outcome || r.was_correct != null);
  if (settled.length) {
    const won = settled.filter((r) => r.was_correct === true).length;
    sections.push({
      heading: `${pair} result and verification`,
      paragraphs: [
        `This match has been settled and graded. Of the ${settled.length} published ${settled.length === 1 ? "selection" : "selections"}, ${won} landed. Statlign grades every pick against the final result — winners and losers alike stay on the page permanently so the published record can be audited rather than curated.`,
        ...settled
          .filter((r) => r.actual_outcome)
          .map(
            (r) =>
              `${marketLabel(resolved, String(r.market_type))}: predicted ${outcomeWithLine(r)}, actual result ${r.actual_outcome}${r.was_correct === true ? " — correct." : r.was_correct === false ? " — incorrect." : "."}`,
          ),
      ],
    });
  } else {
    sections.push({
      heading: "How this pick will be graded",
      paragraphs: [
        `Once the match finishes, Statlign's verification job pulls the final result and grades every selection on this page automatically. The outcome is written back here and rolled into the public track record — no pick is quietly removed, and losing calls stay visible alongside winners.`,
        `That grading pipeline is the reason our published hit rate is meaningful. If a selection settles as a loss, it counts as a loss. You can review the full audited history on the track record page, broken down by sport and market.`,
      ],
    });
  }

  return sections;
}

/** Contextual FAQs for a match page, in the sport's own vocabulary. */
export function sportFixtureFaqs(
  rows: SportFixtureRow[],
  sport?: UsSport,
): { question: string; answer: string }[] {
  const anchor = primaryRow(rows);
  const resolved = sport ?? findUsSport(anchor.sport);
  const label = resolved?.label ?? anchor.sport.toUpperCase();
  const pair = matchTitle(anchor, resolved);
  const pick = outcomeWithLine(anchor);
  const odds = fmtOdds(anchor.odds);
  const markets = [...new Set(rows.map((r) => marketLabel(resolved, String(r.market_type))))];

  return [
    {
      question: `Who does Statlign predict will win ${pair}?`,
      answer: `Our model's headline selection for this ${label} match is ${pick}${odds ? ` at odds of ${odds}` : ""}. The pick is based on the de-vigged market price adjusted for recent form, and the full reasoning is published above.`,
    },
    {
      question: `What markets are covered for ${pair}?`,
      answer: `We currently publish ${markets.join(", ")} for this match. Each market is modelled independently, so the value selection in one market does not imply the same side is value in another.`,
    },
    {
      question: `How accurate are Statlign's ${label} predictions?`,
      answer: `Every published selection is graded against the final result and rolled into a public track record, broken down by sport and market. We do not remove losing picks. No prediction service can guarantee winners — the goal is a positive edge over many bets, not certainty on any single one.`,
    },
    {
      question: `When is ${pair} taking place?`,
      answer: `${utcDate(anchor.commence_time)} at ${utcTime(anchor.commence_time)}. Times are shown in UTC on this page; the app converts them to your local timezone when you are signed in.`,
    },
    {
      question: `Is betting on ${label} risky?`,
      answer: `Yes. All betting carries risk of loss and nothing on this page is financial advice. Only stake money you can afford to lose, set limits in advance, and use the responsible gambling resources linked in our footer if betting stops being enjoyable.`,
    },
  ];
}

/** Rough word count of the generated editorial — used by the depth validator. */
export function editorialWordCount(sections: EditorialSection[]): number {
  return sections
    .flatMap((s) => [s.heading, ...s.paragraphs])
    .join(" ")
    .split(/\s+/)
    .filter(Boolean).length;
}
